import { Icon } from "./Icon";
import { openRowOnClick } from "../lib/rowOpen";
import { COACH_SEES_ENGINE } from "../lib/coachEngine";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { useDraggable } from "@dnd-kit/core";
import { VideoThumb } from "./VideoThumb";
import { href } from "../lib/route";
import { initialView, prefKeyFor, type ShelfSurface, type ShelfView } from "../lib/shelfView";
import { readPref, writePref } from "../lib/viewPrefs";
import { ShelfMore } from "./ShelfMore";
import { ActionStrip } from "./ActionStrip";
import { splitRow, type StripItem } from "../lib/actionStrip";
import {
  exerciseCaption, levelText, trainsText, usedInText, type ShelfRow,
} from "../lib/exerciseShelf";

/* W72 STEP 5 (1) — THE EXERCISE SHELF: ONE component for the Library's Exercises page AND the workout builder's left
 * library pane, in both views (list · grid). His note, 2026-09-25 ~07:05: *"we need in the workout builder left in
 * exercise list a grid view too with 2 exercises each row. So square containers rounded a bit. And a button same line
 * of most recent text but right, if list & grid selecting"*.
 *
 * 🔴 ONE FILE, SO THE PANE AND THE PAGE CANNOT DRIFT. Harness gate `w72-5-the-library` asserts both surfaces mount
 * `<ExerciseShelf` and that no other file draws a shelf row or card.
 *
 * 🔴 EVERY DRAGGABLE CONTAINER KEEPS ITS GRIP ON ITS RIGHT EDGE (w72-3's rule, his 2026-09-25 ~04:30 word). The pane's
 * list row keeps its `::after` grip (`.builder-picker-row`); the pane's grid card draws the same six dots on the right
 * of its name line (`.shelf-card.is-draggable::after`). The page's rows and cards are links, never dragged, and draw
 * no grip.
 *
 * 🔴 NO CHIP INSIDE A ROW (his yes on Proposal 5, *"without these strange chips above or below each exercise row"*):
 * every fact a row states is TEXT from `lib/exerciseShelf.ts`.
 */

/** The view a surface shows: `?ht-view=` pins it, else its own remembered choice, else list. Each surface keeps its
 *  own key, so the pane and the page do not flip each other. */
export function useShelfView(surface: ShelfSurface): [ShelfView, (v: ShelfView) => void] {
  const [view, setView] = useState<ShelfView>(() => {
    // W140: the coach's own remembered choice (`viewPrefs`, never throws); the `?ht-view=` pin still wins.
    return initialView(window.location.search, readPref(prefKeyFor(surface)));
  });
  useEffect(() => {
    writePref(prefKeyFor(surface), view);
  }, [surface, view]);
  return [view, setView];
}

/** The List · Grid switch — two real radios drawn as icons, the label IS the button. The name is real text for a
 *  screen reader (an icon-only pair needs a name more than a labelled one did). */
export function ShelfViewSwitch({ view, onChange, name }: {
  view: ShelfView; onChange: (v: ShelfView) => void; name: string;
}) {
  return (
    <fieldset className="shelf-view">
      <legend className="ht-visually-hidden">View</legend>
      {(["list", "grid"] as const).map((v) => (
        <label key={v} className={`shelf-view-opt${view === v ? " is-on" : ""}`} title={v === "list" ? "List" : "Grid"}>
          <input type="radio" name={name} checked={view === v} onChange={() => onChange(v)} />
          <span className="ht-visually-hidden">{v === "list" ? "List" : "Grid"}</span>
          {v === "list" ? (
            <Icon name="list" size={16} />
          ) : (
            <Icon name="grid" size={16} />
          )}
        </label>
      ))}
    </fieldset>
  );
}

/* ── The two variants ─────────────────────────────────────────────────────────────────────────────── */

/** A row the builder's pane can pick: the picture and the name are all it shows (his "don't show fields in the list
 *  view"). */
export type PaneItem = {
  id: string; name: string; demoVideoUrl: string | null; thumbnailUrl: string | null;
};

type PaneProps = {
  variant: "pane";
  view: ShelfView;
  items: readonly PaneItem[];
  /** False while the workout has several sections and none is chosen — the rows go inert and the grip hides. */
  canAdd: boolean;
  onPick: (id: string) => void;
  /** Week editor stage 2 — the client Week's library panel carries a row with the week's own POINTER carry (`RowDrag.tsx`)
   *  instead of the builder's dnd-kit drag: the Week's dnd-kit area carries WORKOUTS, and a library row registered in it would
   *  wake that drag. A press still picks (`onPick`). */
  onCarry?: (id: string, e: React.PointerEvent) => void;
};

type PageProps = {
  variant: "page";
  view: ShelfView;
  rows: readonly ShelfRow[];
  selected: ReadonlySet<string>;
  onSelect: (next: ReadonlySet<string>) => void;
  onCopy: (id: string) => void;
  onHide: (id: string, hide: boolean) => void;
  /** A row whose Copy/Hide is in flight, so its actions do not fire twice. */
  busy: string | null;
};

export function ExerciseShelf(props: PaneProps | PageProps) {
  return props.variant === "pane" ? <PaneShelf {...props} /> : <PageShelf {...props} />;
}

/* ── The pane ─────────────────────────────────────────────────────────────────────────────────────── */

function PaneShelf({ view, items, canAdd, onPick, onCarry }: PaneProps) {
  /* 🔴 DRAG IS THE VISIBLE MECHANISM AND THE CLICK STAYS — WCAG 2.5.7: a single pointer without dragging adds the
     same exercise, and a real <button> gives Enter and Space for free (2.1.1). Both views carry the identical handlers —
     the view changes the SHAPE, never what a press does. W96: the drag is @dnd-kit's (`LibraryDrag`) — a row lifts after
     a 4 px move, so a click never becomes a drag; the builder's gap appears in the part the pointer enters. */
  if (view === "grid") {
    return (
      <ul className="shelf-grid shelf-grid-pane">
        {items.map((e) => (
          <li key={e.id}>
            {onCarry !== undefined ? (
              <CarryRow id={e.id} onCarry={onCarry} className="shelf-card is-draggable">
                <VideoThumb demoVideoUrl={e.demoVideoUrl} thumbnailUrl={e.thumbnailUrl} size="square" />
                <span className="shelf-card-name">{e.name}</span>
              </CarryRow>
            ) : (
            <LibraryDrag id={e.id} canAdd={canAdd} onPick={onPick} className="shelf-card is-draggable">
              <VideoThumb demoVideoUrl={e.demoVideoUrl} thumbnailUrl={e.thumbnailUrl} size="square" />
              <span className="shelf-card-name">{e.name}</span>
            </LibraryDrag>
            )}
          </li>
        ))}
      </ul>
    );
  }
  return (
    <ul className="builder-picker-list">
      {items.map((e) => (
        <li key={e.id}>
          {onCarry !== undefined ? (
            <CarryRow id={e.id} onCarry={onCarry} className="ht-button ht-button-quiet builder-picker-row">
              <VideoThumb demoVideoUrl={e.demoVideoUrl} thumbnailUrl={e.thumbnailUrl} />
              <span>{e.name}</span>
            </CarryRow>
          ) : (
          <LibraryDrag id={e.id} canAdd={canAdd} onPick={onPick} className="ht-button ht-button-quiet builder-picker-row">
            {/* A PICTURE FIRST; a row with none shows the plate, never initials (his library read "9S", "AR" down the
                column when it drew initials). `alt=""` because the name is right beside it. */}
            <VideoThumb demoVideoUrl={e.demoVideoUrl} thumbnailUrl={e.thumbnailUrl} />
            <span>{e.name}</span>
          </LibraryDrag>
          )}
        </li>
      ))}
    </ul>
  );
}

/** One library row: a button that adds on click and lifts into a drag after a 4 px move (W96). */
function LibraryDrag({ id, canAdd, onPick, className, children }: {
  id: string; canAdd: boolean; onPick: (id: string) => void; className: string; children: ReactNode;
}) {
  const { setNodeRef, listeners, attributes, isDragging } = useDraggable({
    id: `lib:${id}`, disabled: !canAdd, data: { kind: "library", id },
  });
  return (
    <button type="button" ref={setNodeRef} data-lib={id} {...attributes} {...listeners}
            className={isDragging ? `${className} is-lifted` : className}
            onClick={() => onPick(id)} disabled={!canAdd}>
      {children}
    </button>
  );
}

/** The client Week's library row (stage 2): the same look, DRAG ONLY (Ioannis 10-19: "we just drag the exercise … to where we
 *  like") — carried by the Week's pointer carry instead of dnd-kit; not a button, since a press does nothing there (the
 *  keyboard's path is the Week's own `+ Exercise`). */
function CarryRow({ id, onCarry, className, children }: {
  id: string; onCarry: (id: string, e: React.PointerEvent) => void; className: string; children: ReactNode;
}) {
  return (
    <div data-lib={id} className={className} title="Drag onto the week"
         onPointerDown={(e) => { onCarry(id, e); }} onDragStart={(e) => { e.preventDefault(); }}>
      {children}
    </div>
  );
}

/* ── The page ─────────────────────────────────────────────────────────────────────────────────────── */

/** The row's task's own action — drawn on hover (the pencil); every other action is in the row's ONE `⋯` (job 3 step 5
 *  page 4, Fable's brief 2026-10-09: the page's task is FIND an exercise → EDIT it). */
const ON_ROW: readonly string[] = ["edit"];

function PageShelf({ view, rows, selected, onSelect, onCopy, onHide, busy }: PageProps) {
  const [menuFor, setMenuFor] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  /* The `⋯` menu closes on Escape and on a press outside it — a popover a coach can only dismiss by finding the same
     small button again is the thing a coach fights. `mousedown`, not `click`: a re-render on press can detach the
     target before a click bubbles. */
  useEffect(() => {
    if (menuFor === null) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setMenuFor(null); };
    const onDown = (e: MouseEvent) => {
      if (menuRef.current !== null && !menuRef.current.contains(e.target as Node)) setMenuFor(null);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDown);
    return () => { document.removeEventListener("keydown", onKey); document.removeEventListener("mousedown", onDown); };
  }, [menuFor]);

  function toggle(id: string) {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    onSelect(next);
  }

  if (view === "grid") {
    /* Grid is the secondary view: pictures and names only (Proposal 5). A card is a link, never dragged — no grip. */
    return (
      <ul className="shelf-grid shelf-grid-page">
        {rows.map((r) => (
          <li key={r.id}>
            <a className="shelf-card" href={href({ name: "exercise", exerciseId: r.id })}>
              <VideoThumb demoVideoUrl={r.demoVideoUrl} thumbnailUrl={r.thumbnailUrl} size="square" />
              <span className="shelf-card-name">{r.name}</span>
            </a>
          </li>
        ))}
      </ul>
    );
  }

  return (
    <table className={`ours-rows shelf-rows${selected.size > 0 ? " is-selecting" : ""}`}>
      <caption className="ht-visually-hidden">Your exercises</caption>
      <thead>
        <tr>
          <th scope="col" className="ours-col-label">Exercise</th>
          <th scope="col" className="ours-col-label">Trains</th>
          {COACH_SEES_ENGINE && <th scope="col" className="ours-col-label ours-num">Level</th>}
          <th scope="col" className="ours-col-label ours-num">Used in</th>
          <th scope="col"><span className="ht-visually-hidden">Actions</span></th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => {
          const to = href({ name: "exercise", exerciseId: r.id });
          const isSelected = selected.has(r.id);
          const hidden = r.hiddenAt !== null;
          /* 🔴 ONE LIST, the ⋯'s order (W108's rule, here too): the strip draws Edit, the ⋯ the rest — never one action
             twice. Select is the ⋯'s first item: bulk actions appear only after a selection (Proposal 5). */
          const actions = splitRow<StripItem>([
            { key: "select", label: isSelected ? "Deselect" : "Select", onClick: () => toggle(r.id) },
            { key: "edit", label: "Edit", href: to },
            { key: "copy", label: "Copy", disabled: busy === r.id, onClick: () => onCopy(r.id) },
            { key: "hide", label: hidden ? "Unhide" : "Hide", disabled: busy === r.id, onClick: () => onHide(r.id, !hidden) },
          ], ON_ROW);
          return (
            <tr key={r.id} className={`ours-row shelf-row row-opens${isSelected ? " is-selected" : ""}`}
                onClick={openRowOnClick(to)}>
              <th scope="row" className="shelf-who-cell">
                <div className="shelf-who">
                  <VideoThumb demoVideoUrl={r.demoVideoUrl} thumbnailUrl={r.thumbnailUrl} size="mini" />
                  <span className="ours-names">
                    <a className="ours-name" href={to}>{r.name}</a>
                    <span className="ours-caption">{exerciseCaption(r)}</span>
                  </span>
                </div>
              </th>
              <td className="shelf-trains">{trainsText(r.muscles)}</td>
              {COACH_SEES_ENGINE && <td className="ours-num shelf-level">{levelText(r.difficulty)}</td>}
              <td className={`ours-num shelf-used${r.usedIn === 0 ? " is-none" : ""}`}>{usedInText(r.usedIn)}</td>
              <td className="ours-actions shelf-actions">
                {/* HOVER / FOCUS ACTIONS — opacity, never mounted on hover, so the row cannot change height. W119: the ONE
                    `ActionStrip`; job 3 step 5 page 4: it draws Edit (the pencil) alone. */}
                <ActionStrip label={`Actions for ${r.name}`} items={actions.strip} />
                {/* ONCE A SELECTION EXISTS every row offers the same toggle in the open, so picking ten rows is ten
                    presses rather than ten menus. It is not drawn before — no checkbox column (Proposal 5). */}
                {selected.size > 0 && (
                  <button type="button" className="shelf-pick" aria-pressed={isSelected}
                          onClick={() => toggle(r.id)}>
                    {isSelected ? "Selected" : "Select"}
                  </button>
                )}
                {/* The `⋯` holds the rest of the one list — Select · Copy · Hide — and is the door to SELECT: bulk actions
                    appear only after a selection, and there is no checkbox column (Proposal 5). Invisible at rest; its
                    list in the top layer (W85). */}
                <ShelfMore open={menuFor === r.id} label={r.name}
                           wrapRef={menuFor === r.id ? menuRef : undefined}
                           onToggle={() => setMenuFor((m) => (m === r.id ? null : r.id))}>
                  {actions.more.map((a) => (a.href !== undefined
                    ? <a key={a.key} href={a.href}>{a.label}</a>
                    : <button key={a.key} type="button" disabled={a.disabled}
                              onClick={() => { setMenuFor(null); a.onClick?.(); }}>{a.label}</button>))}
                </ShelfMore>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
